<template>
  <el-dialog
    v-model="visibleModel"
    :title="dialogTitle"
    width="880px"
    top="6vh"
    destroy-on-close
    @open="onOpen"
  >
    <el-alert
      v-if="!accounts.length && !loading"
      type="warning"
      :closable="false"
      show-icon
      title="还没有发布账号"
      style="margin-bottom: 12px"
    >
      <template #default>
        请在下方快速添加账号，或到
        <router-link to="/publish">自动发布</router-link>
        页登录 Cookie 后再发布。
      </template>
    </el-alert>

    <div v-if="articlePlatforms.length" class="hint-row">
      <span class="hint-label">文章原始平台：</span>
      <el-tag
        v-for="p in articlePlatforms"
        :key="p"
        size="small"
        type="info"
        style="margin-right: 6px"
      >
        {{ platformLabel(p) }}
      </el-tag>
    </div>

    <!-- 顶栏：搜索 + 快捷选择 + 摘要 -->
    <div class="toolbar">
      <el-input
        v-model="searchText"
        placeholder="搜索账号 ID / 备注 / 分组"
        clearable
        style="width: 260px"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-checkbox v-model="filterByArticlePlatform">
        只显示匹配文章原平台
      </el-checkbox>
      <el-checkbox v-model="onlyHealthy">仅显示可用</el-checkbox>
      <div class="quick-actions">
        <el-dropdown trigger="click" @command="onQuickCommand">
          <el-button type="primary" plain size="default">
            快捷选择
            <el-icon style="margin-left: 4px"><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="all_visible">选中当前可见的全部</el-dropdown-item>
              <el-dropdown-item command="healthy">仅选可用（有 Cookie + 有额度）</el-dropdown-item>
              <el-dropdown-item command="match_platform">按文章原平台匹配</el-dropdown-item>
              <el-dropdown-item command="clear" divided>清空选择</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <div class="summary">
        已选 <b>{{ selected.length }}</b> ·
        待入队约 <b>{{ estimatedJobs }}</b> 条
      </div>
    </div>

    <!-- 按平台分组的账号折叠面板 -->
    <div v-loading="loading" class="platform-groups">
      <el-empty
        v-if="!loading && !groupedAccounts.length"
        description="没有匹配的账号"
        :image-size="80"
      />
      <el-collapse v-model="expandedGroups">
        <el-collapse-item
          v-for="g in groupedAccounts"
          :key="g.platform"
          :name="g.platform"
        >
          <template #title>
            <div class="group-title">
              <el-tag :type="platformTagType(g.platform)" size="small">
                {{ platformLabel(g.platform) }}
              </el-tag>
              <span class="group-count">
                {{ selectedCountByPlatform[g.platform] || 0 }} / {{ g.rows.length }}
              </span>
              <el-checkbox
                :model-value="isAllSelected(g)"
                :indeterminate="isIndeterminate(g)"
                @click.stop
                @change="(v) => toggleGroup(g, v)"
              >
                全选
              </el-checkbox>
              <div class="group-actions" @click.stop>
                <el-radio-group
                  v-model="draftByPlatform[g.platform]"
                  size="small"
                >
                  <el-radio-button :label="true">草稿</el-radio-button>
                  <el-radio-button :label="false">直发</el-radio-button>
                </el-radio-group>
              </div>
            </div>
          </template>

          <el-table
            :data="g.rows"
            :ref="(el) => setTableRef(g.platform, el)"
            max-height="260"
            row-key="uid"
            @selection-change="(rows) => onGroupSelection(g.platform, rows)"
          >
            <el-table-column
              type="selection"
              width="48"
              :selectable="rowSelectable"
            />
            <el-table-column label="账号" min-width="150">
              <template #default="{ row }">
                <div class="acc-cell">
                  <el-avatar
                    v-if="row.avatar_url"
                    :src="row.avatar_url"
                    :size="24"
                    style="margin-right: 6px"
                  />
                  <div class="acc-text">
                    <div class="acc-name">{{ row.display_name || row.account_id }}</div>
                    <div class="acc-sub">{{ row.account_id }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Cookie" width="90">
              <template #default="{ row }">
                <el-tag :type="row.has_cookie ? 'success' : 'danger'" size="small">
                  {{ row.has_cookie ? '有' : '无' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="今日额度" width="110">
              <template #default="{ row }">
                <el-tooltip
                  :content="`已发 ${row.publishes_today || 0} / 上限 ${row.daily_quota || 0}`"
                >
                  <el-progress
                    :percentage="quotaPct(row)"
                    :status="quotaStatus(row)"
                    :stroke-width="10"
                    style="width: 90px"
                  />
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="代理" min-width="130" show-overflow-tooltip>
              <template #default="{ row }">
                <span v-if="row.proxy_display" class="proxy-text">
                  {{ row.proxy_display }}
                </span>
                <span v-else class="proxy-none">未配置</span>
              </template>
            </el-table-column>
            <el-table-column label="上次发布" width="130">
              <template #default="{ row }">
                <el-tooltip
                  v-if="row.last_publish_at"
                  :content="row.last_publish_at"
                >
                  <span class="last-time">{{ relativeTime(row.last_publish_at) }}</span>
                </el-tooltip>
                <span v-else class="last-time-none">从未</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="88">
              <template #default="{ row }">
                <el-tag :type="statusTag(row)" size="small">
                  {{ statusText(row) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- 封面设置：小红书 / 头条 / 百家号 -->
    <div v-if="coverPlatformsNeeded.length" class="cover-panel">
      <div class="cover-panel-title">发布封面</div>
      <p class="cover-hint">
        可为小红书、头条号、百家号单独设置封面；不设置则自动生成。支持上传或一键生成后修改。
      </p>
      <div class="cover-grid">
        <div v-for="p in coverPlatformsNeeded" :key="p" class="cover-card">
          <div class="cover-card-head">
            <el-tag :type="platformTagType(p)" size="small">{{ platformLabel(p) }}</el-tag>
            <span v-if="coversByPlatform[p]?.path" class="cover-ready">已设置</span>
            <span v-else class="cover-pending">未设置（将自动生成）</span>
          </div>
          <div class="cover-preview">
            <img
              v-if="coversByPlatform[p]?.preview_url"
              :src="coversByPlatform[p].preview_url"
              alt="封面预览"
            />
            <div v-else class="cover-empty">暂无封面</div>
          </div>
          <div class="cover-actions">
            <el-upload
              :show-file-list="false"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              :http-request="(opt) => uploadCover(p, opt)"
            >
              <el-button size="small" :loading="coverBusy[p] === 'upload'">上传</el-button>
            </el-upload>
            <el-button
              size="small"
              type="primary"
              plain
              :loading="coverBusy[p] === 'gen'"
              @click="generateCover(p)"
            >生成</el-button>
            <el-button
              size="small"
              text
              type="danger"
              :disabled="!coversByPlatform[p]?.path"
              @click="clearCover(p)"
            >清除</el-button>
          </div>
        </div>
      </div>
    </div>

    <el-divider content-position="left">发布策略</el-divider>
    <el-form label-width="110px" class="strategy-form">
      <el-form-item label="开始时间">
        <el-radio-group v-model="scheduleMode" size="small">
          <el-radio-button label="now">立即</el-radio-button>
          <el-radio-button label="delay">延迟</el-radio-button>
          <el-radio-button label="at">指定时间</el-radio-button>
        </el-radio-group>
        <el-input-number
          v-if="scheduleMode === 'delay'"
          v-model="delayMin"
          :min="1"
          :max="600"
          size="small"
          style="margin-left: 12px; width: 130px"
        />
        <span v-if="scheduleMode === 'delay'" class="unit-hint">分钟后开始</span>
        <el-date-picker
          v-if="scheduleMode === 'at'"
          v-model="scheduledAt"
          type="datetime"
          size="small"
          placeholder="选择日期时间"
          style="margin-left: 12px; width: 220px"
          :disabled-date="disabledPastDate"
        />
      </el-form-item>
      <el-form-item label="错峰间隔">
        <el-input-number
          v-model="staggerSec"
          :min="0"
          :max="3600"
          :step="10"
          size="small"
          style="width: 130px"
        />
        <span class="unit-hint">
          秒，相邻任务间隔以避免风控（0 = 并发发送）
        </span>
      </el-form-item>
      <el-form-item label="草稿默认">
        <el-radio-group v-model="defaultDraftOnly" @change="applyDefaultDraft">
          <el-radio :label="true">全部存草稿</el-radio>
          <el-radio :label="false">全部直发</el-radio>
        </el-radio-group>
        <div class="unit-hint" style="margin-top: 4px">
          单独平台可在上方各分组标题右侧覆盖此默认值
        </div>
      </el-form-item>
    </el-form>

    <el-alert
      v-if="submitWarnings.length"
      type="warning"
      :closable="false"
      show-icon
      style="margin-top: 8px"
    >
      <div v-for="w in submitWarnings" :key="w">· {{ w }}</div>
    </el-alert>

    <el-divider v-if="!accounts.length && !loading">快速添加账号</el-divider>
    <el-form
      v-if="!accounts.length && !loading"
      :inline="true"
      class="quick-add"
    >
      <el-form-item label="平台">
        <el-select v-model="quickAdd.platform" style="width: 130px">
          <el-option label="小红书" value="xiaohongshu" />
          <el-option label="抖音" value="douyin" />
          <el-option label="头条号" value="toutiao" />
          <el-option label="哔哩哔哩" value="bilibili" />
          <el-option label="知乎" value="zhihu" />
          <el-option label="百家号" value="baijiahao" />
        </el-select>
      </el-form-item>
      <el-form-item label="账号ID">
        <el-input
          v-model="quickAdd.account_id"
          placeholder="如 main_01"
          style="width: 120px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="quickSaveAccount">添加</el-button>
        <el-button @click="goLogin">去登录</el-button>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="footer-bar">
        <span class="footer-hint">
          提交后进入发布队列，后台 Worker 自动执行
        </span>
        <div>
          <el-button @click="visibleModel = false">取消</el-button>
          <el-button
            type="primary"
            :loading="submitting"
            :disabled="!selected.length"
            @click="submit"
          >
            开始发布（{{ estimatedJobs }} 条）
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, reactive, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, ArrowDown } from '@element-plus/icons-vue'
import axios from 'axios'

import { PUBLISH_V2 } from '../utils/appBase'
import { articleApi } from '../api'

const router = useRouter()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  articleIds: { type: Array, default: () => [] },
  articlePlatforms: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const V2 = PUBLISH_V2
const COVER_PLATFORMS = ['xiaohongshu', 'toutiao', 'baijiahao']

const PLATFORM_LABELS = {
  xiaohongshu: '小红书',
  douyin: '抖音',
  toutiao: '头条号',
  bilibili: '哔哩哔哩',
  zhihu: '知乎',
  baijiahao: '百家号',
  weixin: '微信公众号',
  lofter: 'LOFTER',
  kuaishou: '快手',
}

const PLATFORM_TAG_TYPES = {
  xiaohongshu: 'danger',
  douyin: '',
  toutiao: 'warning',
  bilibili: 'primary',
  zhihu: 'info',
  baijiahao: 'success',
  weixin: 'success',
  lofter: 'warning',
  kuaishou: 'warning',
}

const visibleModel = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const dialogTitle = computed(() => {
  const n = props.articleIds?.length || 0
  return n > 1 ? `批量发布（${n} 篇）` : `发布文章 #${props.articleIds?.[0] || ''}`
})

const accounts = ref([])
const loading = ref(false)
const submitting = ref(false)
const filterByArticlePlatform = ref(true)
const onlyHealthy = ref(false)
const searchText = ref('')
const expandedGroups = ref([])

// 选择状态：{ [`platform:accId`]: rowRef }
const selectedMap = reactive({})
const selected = computed(() => Object.values(selectedMap))

// 各平台的表格 ref（用于程序化勾选）
const tableRefs = reactive({})
const setTableRef = (platform, el) => {
  if (el) tableRefs[platform] = el
  else delete tableRefs[platform]
}

// 按平台维度的草稿/直发覆盖
const draftByPlatform = reactive({})

// 发布策略
const defaultDraftOnly = ref(true)
const scheduleMode = ref('now') // now | delay | at
const delayMin = ref(5)
const scheduledAt = ref(null)
const staggerSec = ref(30)

const quickAdd = ref({ platform: 'toutiao', account_id: 'default' })

const coversByPlatform = reactive({})
const coverBusy = reactive({})
const articleTitleHint = ref('')

const coverPlatformsNeeded = computed(() => {
  const set = new Set(selected.value.map((r) => r.platform))
  return COVER_PLATFORMS.filter((p) => set.has(p))
})

const platformLabel = (code) => PLATFORM_LABELS[code] || code
const platformTagType = (code) => PLATFORM_TAG_TYPES[code] || ''

const rowUid = (r) => `${r.platform}:${r.account_id}`
const rowSelectable = (row) => row.enabled !== false

const isRowHealthy = (row) => {
  if (row.enabled === false) return false
  if (!row.has_cookie) return false
  const quota = row.daily_quota || 0
  if (quota > 0 && (row.publishes_today || 0) >= quota) return false
  if (row.health_status && row.health_status !== 'ok') return false
  return true
}

const statusTag = (row) => {
  if (!row.has_cookie) return 'danger'
  if (row.health_status === 'ok') return 'success'
  if (row.health_status === 'invalid') return 'danger'
  return 'warning'
}
const statusText = (row) => {
  if (!row.has_cookie) return '未登录'
  if (row.health_status === 'ok') return '正常'
  if (row.health_status === 'invalid') return '失效'
  return row.health_status || '未知'
}

const quotaPct = (row) => {
  const q = row.daily_quota || 0
  const used = row.publishes_today || 0
  if (!q) return used ? 100 : 0
  return Math.min(100, Math.round((used / q) * 100))
}
const quotaStatus = (row) => {
  const p = quotaPct(row)
  if (p >= 100) return 'exception'
  if (p >= 80) return 'warning'
  return 'success'
}

const relativeTime = (iso) => {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  const diff = Date.now() - t
  const min = Math.floor(diff / 60000)
  if (min < 1) return '刚刚'
  if (min < 60) return `${min} 分钟前`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} 小时前`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d} 天前`
  return new Date(iso).toLocaleDateString()
}

const disabledPastDate = (d) => {
  // 允许今天，禁止过去日期
  return d.getTime() < Date.now() - 24 * 3600 * 1000
}

const visibleAccounts = computed(() => {
  const q = searchText.value.trim().toLowerCase()
  const platSet = new Set(props.articlePlatforms || [])
  return accounts.value.filter((a) => {
    if (a.enabled === false) return false
    if (
      filterByArticlePlatform.value
      && platSet.size
      && !platSet.has(a.platform)
    ) return false
    if (onlyHealthy.value && !isRowHealthy(a)) return false
    if (q) {
      const hay = [
        a.account_id,
        a.display_name,
        a.group_name,
      ].filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
})

const groupedAccounts = computed(() => {
  const map = new Map()
  visibleAccounts.value.forEach((a) => {
    const key = a.platform
    if (!map.has(key)) map.set(key, [])
    map.get(key).push({ ...a, uid: rowUid(a) })
  })
  return Array.from(map.entries()).map(([platform, rows]) => ({
    platform,
    rows,
  }))
})

const selectedCountByPlatform = computed(() => {
  const out = {}
  selected.value.forEach((r) => {
    out[r.platform] = (out[r.platform] || 0) + 1
  })
  return out
})

const estimatedJobs = computed(
  () => (selected.value.length || 0) * (props.articleIds?.length || 0)
)

const submitWarnings = computed(() => {
  const w = []
  const noCookie = selected.value.filter((r) => !r.has_cookie).length
  if (noCookie) w.push(`${noCookie} 个已选账号没有登录 Cookie，任务会进入队列但会失败`)
  const overQuota = selected.value.filter(
    (r) => r.daily_quota && (r.publishes_today || 0) >= r.daily_quota
  ).length
  if (overQuota) w.push(`${overQuota} 个账号今日额度已用完`)
  if (scheduleMode.value === 'at' && !scheduledAt.value) {
    w.push('已选"指定时间"，但未选择时间；提交时会退回为立即执行')
  }
  return w
})

const isAllSelected = (g) => {
  if (!g.rows.length) return false
  return g.rows.every((r) => selectedMap[rowUid(r)])
}
const isIndeterminate = (g) => {
  const sel = g.rows.filter((r) => selectedMap[rowUid(r)]).length
  return sel > 0 && sel < g.rows.length
}

const toggleGroup = (g, val) => {
  const tbl = tableRefs[g.platform]
  if (!tbl) return
  g.rows.forEach((r) => {
    if (val && rowSelectable(r)) {
      tbl.toggleRowSelection(r, true)
    } else if (!val) {
      tbl.toggleRowSelection(r, false)
    }
  })
}

const onGroupSelection = (platform, rows) => {
  // 先清掉本 platform 的旧选择，再写入新的
  Object.keys(selectedMap).forEach((k) => {
    if (k.startsWith(`${platform}:`)) delete selectedMap[k]
  })
  rows.forEach((r) => {
    selectedMap[rowUid(r)] = r
  })
}

const onQuickCommand = async (cmd) => {
  await nextTick()
  if (cmd === 'clear') {
    Object.keys(tableRefs).forEach((p) => tableRefs[p]?.clearSelection())
    Object.keys(selectedMap).forEach((k) => delete selectedMap[k])
    return
  }
  groupedAccounts.value.forEach((g) => {
    const tbl = tableRefs[g.platform]
    if (!tbl) return
    g.rows.forEach((row) => {
      if (!rowSelectable(row)) return
      let pick = false
      if (cmd === 'all_visible') pick = true
      else if (cmd === 'healthy') pick = isRowHealthy(row)
      else if (cmd === 'match_platform') {
        pick = (props.articlePlatforms || []).includes(row.platform)
          && row.has_cookie
      }
      tbl.toggleRowSelection(row, pick)
    })
  })
}

const applyDefaultDraft = (val) => {
  groupedAccounts.value.forEach((g) => {
    draftByPlatform[g.platform] = val
  })
}

const loadAccounts = async () => {
  loading.value = true
  try {
    const res = await axios.get(`${V2}/accounts`, {
      params: { enabled_only: false },
    })
    accounts.value = (res.data.items || []).filter(
      (a) => a.enabled !== false
    )
  } catch (e) {
    ElMessage.error('加载账号失败')
  } finally {
    loading.value = false
  }
}

const onOpen = async () => {
  searchText.value = ''
  filterByArticlePlatform.value = true
  onlyHealthy.value = false
  defaultDraftOnly.value = true
  scheduleMode.value = 'now'
  delayMin.value = 5
  scheduledAt.value = null
  staggerSec.value = 30
  articleTitleHint.value = ''
  Object.keys(selectedMap).forEach((k) => delete selectedMap[k])
  Object.keys(draftByPlatform).forEach((k) => delete draftByPlatform[k])
  Object.keys(coversByPlatform).forEach((k) => delete coversByPlatform[k])
  Object.keys(coverBusy).forEach((k) => delete coverBusy[k])

  await loadAccounts()
  // 初始化按平台默认草稿
  groupedAccounts.value.forEach((g) => {
    draftByPlatform[g.platform] = true
  })
  // 默认展开有匹配平台的分组
  const matchSet = new Set(props.articlePlatforms || [])
  expandedGroups.value = groupedAccounts.value
    .map((g) => g.platform)
    .filter((p) => !matchSet.size || matchSet.has(p))

  // 取首篇标题用于封面生成
  try {
    const firstId = props.articleIds?.[0]
    if (firstId) {
      const art = await articleApi.getById(firstId)
      const data = art?.data || art
      articleTitleHint.value = data?.title || ''
      const rd = data?.review_details || {}
      const saved = rd.publish_covers || {}
      for (const p of COVER_PLATFORMS) {
        if (saved[p]) {
          const name = String(saved[p]).split(/[/\\]/).pop()
          coversByPlatform[p] = {
            path: saved[p],
            preview_url: `${V2}/media/cover-file?path=${encodeURIComponent(name)}`,
          }
        }
      }
    }
  } catch (_) { /* ignore */ }

  // 默认勾选：有 Cookie 且平台匹配的账号
  await nextTick()
  setTimeout(() => {
    groupedAccounts.value.forEach((g) => {
      if (matchSet.size && !matchSet.has(g.platform)) return
      const tbl = tableRefs[g.platform]
      if (!tbl) return
      g.rows.forEach((r) => {
        if (isRowHealthy(r)) tbl.toggleRowSelection(r, true)
      })
    })
  }, 120)
}

const uploadCover = async (platform, opt) => {
  const file = opt?.file
  if (!file) return
  coverBusy[platform] = 'upload'
  try {
    const fd = new FormData()
    fd.append('file', file)
    const res = await axios.post(
      `${V2}/media/cover-upload?platform=${encodeURIComponent(platform)}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    const data = res.data || {}
    coversByPlatform[platform] = {
      path: data.path,
      preview_url: data.preview_url,
    }
    ElMessage.success(`${platformLabel(platform)} 封面已上传`)
    opt?.onSuccess?.(data)
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '上传失败')
    opt?.onError?.(e)
  } finally {
    coverBusy[platform] = ''
  }
}

const generateCover = async (platform) => {
  coverBusy[platform] = 'gen'
  try {
    const res = await axios.post(`${V2}/media/cover-generate`, {
      title: articleTitleHint.value || `文章 #${props.articleIds?.[0] || ''}`,
      platform,
      content: '',
    })
    const data = res.data || {}
    coversByPlatform[platform] = {
      path: data.path,
      preview_url: data.preview_url,
    }
    ElMessage.success(`${platformLabel(platform)} 封面已生成，可再上传替换`)
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '生成失败')
  } finally {
    coverBusy[platform] = ''
  }
}

const clearCover = (platform) => {
  delete coversByPlatform[platform]
}

const quickSaveAccount = async () => {
  if (!quickAdd.value.account_id?.trim()) {
    ElMessage.warning('请填写账号 ID')
    return
  }
  try {
    await axios.post(`${V2}/accounts`, {
      platform: quickAdd.value.platform,
      account_id: quickAdd.value.account_id.trim(),
      min_interval_sec: 120,
      daily_quota: 30,
      enabled: true,
    })
    ElMessage.success('账号已添加，请完成登录后再发布')
    await loadAccounts()
    quickAdd.value.account_id = ''
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '添加失败')
  }
}

const goLogin = () => {
  visibleModel.value = false
  router.push('/publish')
}

const buildDraftByPlatform = () => {
  // 只把和默认值不同的平台传给后端，减小 payload
  const out = {}
  const def = defaultDraftOnly.value
  Object.entries(draftByPlatform).forEach(([p, v]) => {
    if (typeof v === 'boolean' && v !== def) out[p] = v
  })
  return out
}

const resolveScheduleFields = () => {
  if (scheduleMode.value === 'delay' && delayMin.value > 0) {
    return { scheduled_offset_sec: Math.round(delayMin.value * 60) }
  }
  if (scheduleMode.value === 'at' && scheduledAt.value) {
    const d = new Date(scheduledAt.value)
    return { scheduled_at: d.toISOString() }
  }
  return {}
}

const submit = async () => {
  if (!selected.value.length) {
    ElMessage.warning('请至少选择一个账号')
    return
  }

  if (submitWarnings.value.length) {
    try {
      await ElMessageBox.confirm(
        `有 ${submitWarnings.value.length} 项警告，是否继续入队？`,
        '发布前确认',
        {
          confirmButtonText: '继续',
          cancelButtonText: '返回修改',
          type: 'warning',
        }
      )
    } catch {
      return
    }
  }

  const targets = selected.value.map((r) => ({
    platform: r.platform,
    account_id: r.account_id,
  }))
  const ids = props.articleIds || []
  if (!ids.length) return

  const draftByPlat = buildDraftByPlatform()
  const scheduleFields = resolveScheduleFields()
  const covers = {}
  for (const p of COVER_PLATFORMS) {
    if (coversByPlatform[p]?.path) covers[p] = coversByPlatform[p].path
  }

  submitting.value = true
  let campaignId = null
  try {
    if (ids.length === 1) {
      const res = await axios.post(`${V2}/enqueue`, {
        article_id: ids[0],
        targets,
        draft_only: defaultDraftOnly.value,
        stagger_sec: staggerSec.value,
        draft_by_platform: Object.keys(draftByPlat).length ? draftByPlat : null,
        covers: Object.keys(covers).length ? covers : null,
        ...scheduleFields,
      })
      campaignId = res.data?.campaign_id || null
      ElMessage.success(`已加入队列 ${res.data.queued} 条`)
    } else {
      const res = await axios.post(`${V2}/enqueue/batch`, {
        article_ids: ids,
        targets,
        draft_only: defaultDraftOnly.value,
        stagger_sec: staggerSec.value,
        draft_by_platform: Object.keys(draftByPlat).length ? draftByPlat : null,
        covers: Object.keys(covers).length ? covers : null,
        ...scheduleFields,
      })
      campaignId = res.data?.campaign_id || null
      ElMessage.success(`已加入队列 ${res.data.queued} 条`)
    }
    visibleModel.value = false
    emit('submitted', { campaignId })
    // 跳转到发布网关并高亮本批
    if (campaignId) {
      try {
        await ElMessageBox.confirm(
          '任务已入队，是否跳转到发布队列查看本批进度？',
          '查看进度',
          {
            confirmButtonText: '查看本批进度',
            cancelButtonText: '留在当前页',
            type: 'success',
          }
        )
        router.push({
          path: '/publish',
          query: { tab: 'queue', campaign_id: campaignId },
        })
      } catch { /* 用户取消 */ }
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || e.message || '入队失败')
  } finally {
    submitting.value = false
  }
}

watch(
  () => props.modelValue,
  (v) => {
    if (v) onOpen()
  }
)
</script>

<style scoped>
.hint-row {
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
}
.hint-label {
  color: #909399;
  margin-right: 4px;
}
.toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 6px;
}
.quick-actions {
  display: flex;
  gap: 8px;
}
.summary {
  margin-left: auto;
  font-size: 13px;
  color: #303133;
}
.summary b {
  color: #409eff;
  font-weight: 600;
  margin: 0 2px;
}
.platform-groups {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 4px 12px;
}
.group-title {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
}
.group-count {
  color: #909399;
  font-size: 13px;
  min-width: 60px;
}
.group-actions {
  margin-left: auto;
  margin-right: 12px;
}
.acc-cell {
  display: flex;
  align-items: center;
}
.acc-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.acc-name {
  font-size: 13px;
  color: #303133;
}
.acc-sub {
  font-size: 11px;
  color: #909399;
}
.proxy-text {
  font-family: monospace;
  font-size: 12px;
  color: #303133;
}
.proxy-none {
  font-size: 12px;
  color: #c0c4cc;
}
.last-time {
  font-size: 12px;
  color: #606266;
}
.last-time-none {
  font-size: 12px;
  color: #c0c4cc;
}
.strategy-form {
  margin-top: 6px;
}
.unit-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.footer-hint {
  font-size: 12px;
  color: #909399;
}
.cover-panel {
  margin: 14px 0 4px;
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafbfc;
}
.cover-panel-title {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
}
.cover-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #909399;
}
.cover-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.cover-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px;
}
.cover-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
}
.cover-ready { color: #67c23a; }
.cover-pending { color: #909399; }
.cover-preview {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 6px;
  overflow: hidden;
  background: #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cover-empty {
  color: #c0c4cc;
  font-size: 12px;
}
.cover-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
}
</style>
